import { useCommonRouter } from '@repo/platform';
import { BoxButton, MainTitle, StickyBottomWrapper, useDialog } from '@repo/ui';
import { useAuthContext } from '@repo/user-session';
import CryptoJS from 'crypto-js';
import { createPortal } from 'react-dom';

import { BZNAV_EMAIL_SIGN_UP_AES_KEY } from '../constants';
import { useFormValidation } from '../hooks/use-form-validation';
import { FormFieldMap, FieldCategory } from '../types';
import { checkEmailValidation, checkPasswordConfirmValidation, checkPasswordValidation, checkPhoneNumberValidation, checkUsernameValidation } from '../utils';

import { FormField } from './SignItems';

export const SignUpInput = () => {
  const allFields: FieldCategory[] = ['email', 'password', 'passwordConfirm', 'name', 'phoneNumber'];
  const fieldForm: Record<FieldCategory, FormFieldMap> = {
    email: {
      label: '이메일 주소',
      placeholder: 'example@email.com',
      type: 'email',
      autoComplete: 'email',
      validation: checkEmailValidation
    },
    name: {
      label: '이름',
      placeholder: '홍길동',
      type: 'text',
      autoComplete: 'name',
      validation: checkUsernameValidation
    },
    password: {
      label: '비밀번호',
      placeholder: '영문, 숫자, 특수문자 포함 8자 이상',
      type: 'password',
      autoComplete: 'new-password',
      validation: checkPasswordValidation
    },
    passwordConfirm: {
      label: '비밀번호 확인',
      placeholder: '비밀번호를 다시 한 번 입력해주세요',
      type: 'password',
      autoComplete: 'new-password',
      validation: checkPasswordConfirmValidation
    },
    phoneNumber: {
      label: '휴대폰 번호',
      placeholder: '010-1234-5678',
      type: 'tel',
      autoComplete: 'tel-national',
      validation: checkPhoneNumberValidation
    }
  };
  const router = useCommonRouter();
  const { authService } = useAuthContext();
  const { showDialog } = useDialog();
  const { fields } = useFormValidation(allFields);
  const isAllFieldsValid = allFields.every(
    (field) =>
      fields[field]?.inputState.value &&
      fieldForm[field].validation(fields[field]?.inputState.value, field === 'passwordConfirm' ? fields.password?.inputState.value : undefined).isValid
  );
  const footerElement = document.getElementById('bznav-footer');

  const goNextSignUpStep = async () => {
    let isValid = true;
    allFields.forEach((field) => {
      const isFieldValid = fields[field]?.validate(fieldForm[field].validation, field === 'passwordConfirm' ? fields.password?.inputState.value : undefined);
      if (!isFieldValid) isValid = false;
    });

    if (!isValid || !authService) {
      return;
    }

    const isEmailExist = await authService?.signService.checkEmailExists(fields.email?.inputState.value ?? '');

    if (isEmailExist) {
      fields.email?.clear();
      fields.email?.setErrorMessage('사용할 수 없는 이메일 입니다.');
      showDialog({
        title: '이미 가입된 이메일이에요',
        description: '다른 이메일을 사용하시거나 비밀번호 찾기를 이용해주세요.'
      });
      return;
    }

    // AES 암호화를 위한 256비트 키를 hex 문자열로 생성
    const aesEncryptKey = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    localStorage.setItem(BZNAV_EMAIL_SIGN_UP_AES_KEY, aesEncryptKey);
    const encryptedState = CryptoJS.AES.encrypt(
      encodeURIComponent(
        JSON.stringify({
          email: fields.email?.inputState.value,
          name: fields.name?.inputState.value,
          password: fields.password?.inputState.value,
          phone: fields.phoneNumber?.inputState.value.replace(/-/g, '')
        })
      ),
      aesEncryptKey
    ).toString();

    router.push(`${authService.signPaths.signUpTerms}?state=${encryptedState}`);
  };

  return (
    <>
      <MainTitle title="필수 정보를 입력해주세요" />
      <form>
        {allFields.map((field) => {
          if (fields[field]) {
            const filedFormItem = fieldForm[field];
            return (
              <FormField
                key={field}
                inputState={fields[field].inputState}
                onValueChange={fields[field].setValue}
                checkValidation={() =>
                  fields[field]?.validate(filedFormItem.validation, field === 'passwordConfirm' ? fields.password?.inputState.value : undefined) ?? false
                }
                onKeyupEnter={goNextSignUpStep}
                label={filedFormItem.label}
                placeholder={filedFormItem.placeholder}
                type={filedFormItem.type}
                autoComplete={filedFormItem.autoComplete}
              />
            );
          }
          return null;
        })}
      </form>

      {footerElement &&
        createPortal(
          <StickyBottomWrapper className={'!sticky'}>
            <BoxButton size={'large'} className={'w-full'} disabled={!isAllFieldsValid} onClick={goNextSignUpStep}>
              이메일로 가입하기
            </BoxButton>
          </StickyBottomWrapper>,
          footerElement
        )}
    </>
  );
};
